
import React, { useState, useEffect, useRef } from "react";
import { Box, HStack, Text, VStack, Pressable, useMediaQuery, ScrollView } from "native-base";
import Svg from "react-native-svg";
import CustomButton from "../../../../components/button";
import InputField from "../../../../components/input";
import Dropdown from "../../../../components/dropdown";
import CustomModal from "../../../../components/modal";
import MobileModelSheet from "../../../../components/actionSheet";
import { deleteCardsIconSVG, addIconSVG } from "../../../../utils/svg";
import { COLORS } from "../../../../utils/colors";
import { COMMONFONTSIZE, COMMONFONTWEIGHT } from "../../../../utils/fonts";
import { MOBILE_VIEW_WIDTH } from "../../../../utils/screens";

const PlusButton = ({ onPress, disabled }) => (
    <Pressable
        onPress={disabled ? undefined : (onPress || (() => { }))}
        p={1.5}
        borderRadius="md"
        bg={COLORS.THEME_COLOR}
        ml={1}
        mt='5px'
        opacity={disabled ? 0.5 : 1}
        _pressed={{ bg: COLORS.THEME_COLOR, opacity: 0.7 }}
    >
        <Svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            {addIconSVG}
        </Svg>
    </Pressable>
);


const createEmptyRow = () => ({
    id: Date.now() + Math.random(),
    head: "",
    sub_head: "",
    details: "",
    size: "",
    quantity: "",
    price: "",
    remarks: "",
    showHead: true,
    showSubHead: true,
    showDetails: true,
});

const AddNewBenchmarkModal = ({ isOpen, onClose, onSubmit, isLoading = false }) => {
    const [MobileWindow] = useMediaQuery({ maxWidth: MOBILE_VIEW_WIDTH });
    const [addRowCount, setAddRowCount] = useState("5");
    const [rows, setRows] = useState(Array.from({ length: 5 }, () => createEmptyRow()));
    const [fieldErrors, setFieldErrors] = useState({});
    const mobileSheetRef = useRef(null);

    const normalizeText = (value) => (value ?? "").toString().trim();
    const isZeroValue = (value) => {
        const normalized = normalizeText(value).replace(/,/g, "");
        if (!normalized) return false;
        const parsed = Number(normalized);
        return Number.isFinite(parsed) && parsed === 0;
    };

    // Reset rows when modal opens
    useEffect(() => {
        if (isOpen) {
            setRows(Array.from({ length: 5 }, () => createEmptyRow()));
            setAddRowCount("5");
            setFieldErrors({});

            // Scroll to top similar to other modals
            if (MobileWindow && mobileSheetRef.current) {
                setTimeout(() => {
                    if (mobileSheetRef.current?.scrollToTop) {
                        mobileSheetRef.current.scrollToTop();
                    }
                }, 100);
            } else {
                window.scrollTo({ top: 0, behavior: "smooth" });
            }
        }
    }, [isOpen, MobileWindow]);

    const addRowCountOptions = [
        { label: "1", value: "1" },
        { label: "5", value: "5" },
        { label: "10", value: "10" },
    ];

    const handleAddRows = () => {
        const count = parseInt(addRowCount) || 1;
        const newRows = Array.from({ length: count }, () => createEmptyRow());
        setRows([...rows, ...newRows]);
    };

    const removeRow = (index) => {
        const newRows = [...rows];
        const removedRow = newRows[index];
        newRows.splice(index, 1);
        setRows(newRows);
        if (removedRow?.id) {
            setFieldErrors((prev) => {
                const next = { ...prev };
                delete next[removedRow.id];
                return next;
            });
        }
    };

    const updateRow = (index, field, value) => {
        const newRows = [...rows];
        newRows[index] = { ...newRows[index], [field]: value };
        setRows(newRows);

        if (field === "quantity" || field === "price") {
            const rowId = newRows[index]?.id;
            if (!rowId) return;
            const hasZero = isZeroValue(value);
            setFieldErrors((prev) => ({
                ...prev,
                [rowId]: { ...prev[rowId], [field]: hasZero }
            }));
        }
    };

    const addRowWithSameHead = (index) => {
        const currentRow = rows[index];
        if (!currentRow?.head) return;

        const newRow = {
            ...createEmptyRow(),
            head: currentRow.head,
            showHead: false,
            showSubHead: true,
            showDetails: true,
        };

        const newRows = [...rows];
        newRows.splice(index + 1, 0, newRow);
        setRows(newRows);
    };

    const addRowWithSameHeadSubHead = (index) => {
        const currentRow = rows[index];
        if (!currentRow?.head || !currentRow?.sub_head) return;

        const newRow = {
            ...createEmptyRow(),
            head: currentRow.head,
            sub_head: currentRow.sub_head,
            showHead: false,
            showSubHead: false,
            showDetails: true,
        };

        const newRows = [...rows];
        newRows.splice(index + 1, 0, newRow);
        setRows(newRows);
    };

    const addRowWithSameDetails = (index) => {
        const currentRow = rows[index];
        if (!currentRow?.head || !currentRow?.sub_head || !currentRow?.details) return;

        const newRow = {
            ...createEmptyRow(),
            head: currentRow.head,
            sub_head: currentRow.sub_head,
            details: currentRow.details,
            showHead: false,
            showSubHead: false,
            showDetails: false,
        };

        const newRows = [...rows];
        newRows.splice(index + 1, 0, newRow);
        setRows(newRows);
    };

    const handleSubmit = () => {
        const nextErrors = {};
        rows.forEach((row) => {
            const quantityZero = isZeroValue(row.quantity);
            const priceZero = isZeroValue(row.price);
            if (quantityZero || priceZero) {
                nextErrors[row.id] = {
                    quantity: quantityZero,
                    price: priceZero
                };
            }
        });
        setFieldErrors(nextErrors);
        if (Object.keys(nextErrors).length > 0) return;

        onSubmit(rows);
    };

    const getModalHeader = () => (
        <Text fontWeight={COMMONFONTWEIGHT.LARGE_FONT_WEIGHT}>
            Add New Benchmark
        </Text>
    );

    const getModalBody = () => (
        <ScrollView showsVerticalScrollIndicator={false}>
            <Box>
                <HStack justifyContent="space-between" alignItems="center" mb={3} flexWrap={MobileWindow ? "wrap" : "nowrap"} space={3}>
                    <Text fontSize={COMMONFONTSIZE.LARGE_MEDIUM_FONT_SIZE} fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}>Benchmark Details</Text>
                    <HStack alignItems="center" space={2} flexWrap={MobileWindow ? "wrap" : "nowrap"}>
                        <Box width={MobileWindow ? "100px" : "80px"}>
                            <Dropdown
                                name="add_row_count"
                                selectedValues={addRowCount}
                                setSelectedValues={setAddRowCount}
                                dropItems={addRowCountOptions}
                                placeholder="5"
                                width="100%"
                            />
                        </Box>
                        <CustomButton
                            text={`Add ${addRowCount} Row${parseInt(addRowCount) > 1 ? 's' : ''}`}
                            onPress={handleAddRows}
                            bgColor={COLORS.THEME_COLOR}
                            textColor={COLORS.WHITE_COLOR}
                            size="sm"
                        />
                    </HStack>
                </HStack>

                {/* Table Container */}
                <Box
                    borderWidth={1}
                    borderColor={COLORS.BORDER_COLOR}
                    borderRadius="md"
                    overflow="hidden"
                    bg={COLORS.WHITE_COLOR}
                >
                    {/* Table Header */}
                    <HStack
                        bg={COLORS.BORDER_COLOR}
                        py={2}
                        px={2}
                        borderBottomWidth={1}
                        borderBottomColor={COLORS.BORDER_COLOR}
                        display={MobileWindow ? "none" : "flex"}
                    >
                        <Box flex={1.5} px={2}>
                            <Text fontSize="sm" fontWeight="bold">Head</Text>
                        </Box>
                        <Box flex={1.5} px={2}>
                            <Text fontSize="sm" fontWeight="bold">Sub Head</Text>
                        </Box>
                        <Box flex={2} px={2}>
                            <Text fontSize="sm" fontWeight="bold">Details</Text>
                        </Box>
                        <Box flex={1} px={2}>
                            <Text fontSize="sm" fontWeight="bold">Size</Text>
                        </Box>
                        <Box flex={1} px={2}>
                            <Text fontSize="sm" fontWeight="bold">Quantity</Text>
                        </Box>
                        <Box flex={1.5} px={2}>
                            <Text fontSize="sm" fontWeight="bold">Benchmark Price</Text>
                        </Box>
                        <Box flex={1.5} px={2}>
                            <Text fontSize="sm" fontWeight="bold">Remarks</Text>
                        </Box>
                        {rows.length > 1 && (
                            <Box flex={0.5} px={2} alignItems="center" justifyContent="center">
                                <Text fontSize="sm" fontWeight="bold">Action</Text>
                            </Box>
                        )}
                    </HStack>

                    {/* Table Body */}
                    <VStack divider={<Box borderBottomWidth={1} borderColor={COLORS.BORDER_COLOR} />}>
                        {rows.map((row, index) => (
                            <Box
                                key={row.id || `row-${index}`}
                                bg={index % 2 === 0 ? COLORS.WHITE_COLOR : "gray.50"}
                            >
                                {MobileWindow ? (
                                    /* Mobile View: Stacked Layout */
                                    <VStack p={3} space={3}>
                                        <HStack justifyContent="space-between" alignItems="center">
                                            <Text fontSize="sm" fontWeight="bold" flex={1}>
                                                Entry {index + 1}
                                            </Text>
                                            {rows.length > 1 && (
                                                <Pressable
                                                    onPress={() => removeRow(index)}
                                                    p={1}
                                                    borderRadius="md"
                                                    _pressed={{ bg: COLORS.BORDER_COLOR }}
                                                >
                                                    <Svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                                        {deleteCardsIconSVG}
                                                    </Svg>
                                                </Pressable>
                                            )}
                                        </HStack>

                                        <HStack space={1} alignItems="flex-end" width="100%">
                                            <Box flex={1}>
                                                {row.showHead !== false ? (
                                                    <InputField
                                                        label="Head"
                                                        name={`head_${index}`}
                                                        value={row.head || ""}
                                                        onChange={(e, name, ind) => updateRow(index, "head", e.target.value)}
                                                        placeholder="Enter Head"
                                                        width="100%"
                                                    />
                                                ) : (
                                                    <Box>
                                                        <Text fontSize="xs" color={COLORS.LABEL_TEXT_COLOR} mb={1}>Head</Text>
                                                        <Box height="37px" />
                                                    </Box>
                                                )}
                                            </Box>
                                            {row.showHead !== false && (
                                                <PlusButton
                                                    onPress={() => addRowWithSameHead(index)}
                                                    disabled={!row.head}
                                                />
                                            )}
                                        </HStack>
                                        <HStack space={1} alignItems="flex-end" width="100%">
                                            <Box flex={1}>
                                                {row.showSubHead !== false ? (
                                                    <InputField
                                                        label="Sub Head"
                                                        name={`sub_head_${index}`}
                                                        value={row.sub_head || ""}
                                                        onChange={(e, name, ind) => updateRow(index, "sub_head", e.target.value)}
                                                        placeholder="Enter Sub Head"
                                                        width="100%"
                                                    />
                                                ) : (
                                                    <Box>
                                                        <Text fontSize="xs" color={COLORS.LABEL_TEXT_COLOR} mb={1}>Sub Head</Text>
                                                        <Box height="37px" />
                                                    </Box>
                                                )}
                                            </Box>
                                            {row.showSubHead !== false && (
                                                <PlusButton
                                                    onPress={() => addRowWithSameHeadSubHead(index)}
                                                    disabled={!row.head || !row.sub_head}
                                                />
                                            )}
                                        </HStack>
                                        <HStack space={1} alignItems="flex-end" width="100%">
                                            <Box flex={1}>
                                                {row.showDetails !== false ? (
                                                    <InputField
                                                        label="Details"
                                                        name={`details_${index}`}
                                                        value={row.details || ""}
                                                        onChange={(e, name, ind) => updateRow(index, "details", e.target.value)}
                                                        placeholder="Enter Details"
                                                        width="100%"
                                                    />
                                                ) : (
                                                    <Box>
                                                        <Text fontSize="xs" color={COLORS.LABEL_TEXT_COLOR} mb={1}>Details</Text>
                                                        <Box height="37px" />
                                                    </Box>
                                                )}
                                            </Box>
                                            {row.showDetails !== false && (
                                                <PlusButton
                                                    onPress={() => addRowWithSameDetails(index)}
                                                    disabled={!row.head || !row.sub_head || !row.details}
                                                />
                                            )}
                                        </HStack>
                                        <HStack space={2}>
                                            <Box flex={1}>
                                                <InputField
                                                    label="Size"
                                                    name={`size_${index}`}
                                                    value={row.size || ""}
                                                    onChange={(e, name, ind) => updateRow(index, "size", e.target.value)}
                                                    placeholder="Enter Size"
                                                    width="100%"
                                                />
                                            </Box>
                                            <Box flex={1}>
                                                <InputField
                                                    label="Quantity"
                                                    name={`quantity_${index}`}
                                                    value={row.quantity || ""}
                                                    onChange={(e, name, ind) => updateRow(index, "quantity", e.target.value)}
                                                    placeholder="Enter Qty"
                                                    keyboardType="numeric"
                                                    width="100%"
                                                    isInvalid={!!fieldErrors[row.id]?.quantity}
                                                    errorMessage="Invalid"
                                                />
                                            </Box>
                                        </HStack>
                                        <InputField
                                            label="Benchmark Price"
                                            name={`price_${index}`}
                                            value={row.price || ""}
                                            onChange={(e, name, ind) => updateRow(index, "price", e.target.value)}
                                            placeholder="Enter Price"
                                            keyboardType="numeric"
                                            width="100%"
                                            isInvalid={!!fieldErrors[row.id]?.price}
                                            errorMessage="Invalid"
                                        />
                                        <InputField
                                            label="Remarks"
                                            name={`remarks_${index}`}
                                            value={row.remarks || ""}
                                            onChange={(e, name, ind) => updateRow(index, "remarks", e.target.value)}
                                            placeholder="Enter Remarks"
                                            width="100%"
                                        />
                                    </VStack>
                                ) : (
                                    /* Desktop View: Table Row */
                                    <HStack py={2} px={2} alignItems="flex-start" space={0}>
                                        <Box flex={1.5} px={1}>
                                            <HStack space={1} alignItems="center" width="100%">
                                                <Box flex={1}>
                                                    {row.showHead !== false ? (
                                                        <InputField
                                                            name={`head_${index}`}
                                                            value={row.head || ""}
                                                            onChange={(e, name, ind) => updateRow(index, "head", e.target.value)}
                                                            placeholder="Head"
                                                            width="100%"
                                                        />
                                                    ) : (
                                                        <Box height="37px" />
                                                    )}
                                                </Box>
                                                {row.showHead !== false && (
                                                    <PlusButton
                                                        onPress={() => addRowWithSameHead(index)}
                                                        disabled={!row.head}
                                                    />
                                                )}
                                            </HStack>
                                        </Box>
                                        <Box flex={1.5} px={1}>
                                            <HStack space={1} alignItems="center" width="100%">
                                                <Box flex={1}>
                                                    {row.showSubHead !== false ? (
                                                        <InputField
                                                            name={`sub_head_${index}`}
                                                            value={row.sub_head || ""}
                                                            onChange={(e, name, ind) => updateRow(index, "sub_head", e.target.value)}
                                                            placeholder="Sub Head"
                                                            width="100%"
                                                        />
                                                    ) : (
                                                        <Box height="37px" />
                                                    )}
                                                </Box>
                                                {row.showSubHead !== false && (
                                                    <PlusButton
                                                        onPress={() => addRowWithSameHeadSubHead(index)}
                                                        disabled={!row.head || !row.sub_head}
                                                    />
                                                )}
                                            </HStack>
                                        </Box>
                                        <Box flex={2} px={1}>
                                            <HStack space={1} alignItems="center" width="100%">
                                                <Box flex={1}>
                                                    {row.showDetails !== false ? (
                                                        <InputField
                                                            name={`details_${index}`}
                                                            value={row.details || ""}
                                                            onChange={(e, name, ind) => updateRow(index, "details", e.target.value)}
                                                            placeholder="Details"
                                                            width="100%"
                                                        />
                                                    ) : (
                                                        <Box height="37px" />
                                                    )}
                                                </Box>
                                                {row.showDetails !== false && (
                                                    <PlusButton
                                                        onPress={() => addRowWithSameDetails(index)}
                                                        disabled={!row.head || !row.sub_head || !row.details}
                                                    />
                                                )}
                                            </HStack>
                                        </Box>
                                        <Box flex={1} px={1}>
                                            <InputField
                                                name={`size_${index}`}
                                                value={row.size || ""}
                                                onChange={(e, name, ind) => updateRow(index, "size", e.target.value)}
                                                placeholder="Size"
                                                width="100%"
                                            />
                                        </Box>
                                        <Box flex={1} px={1}>
                                            <InputField
                                                name={`quantity_${index}`}
                                                value={row.quantity || ""}
                                                onChange={(e, name, ind) => updateRow(index, "quantity", e.target.value)}
                                                placeholder="Quantity"
                                                keyboardType="numeric"
                                                width="100%"
                                                isInvalid={!!fieldErrors[row.id]?.quantity}
                                                errorMessage="Invalid"
                                            />
                                        </Box>
                                        <Box flex={1.5} px={1}>
                                            <InputField
                                                name={`price_${index}`}
                                                value={row.price || ""}
                                                onChange={(e, name, ind) => updateRow(index, "price", e.target.value)}
                                                placeholder="Price"
                                                keyboardType="numeric"
                                                width="100%"
                                                isInvalid={!!fieldErrors[row.id]?.price}
                                                errorMessage="Invalid"
                                            />
                                        </Box>
                                        <Box flex={1.5} px={1}>
                                            <InputField
                                                name={`remarks_${index}`}
                                                value={row.remarks || ""}
                                                onChange={(e, name, ind) => updateRow(index, "remarks", e.target.value)}
                                                placeholder="Remarks"
                                                width="100%"
                                            />
                                        </Box>
                                        {rows.length > 1 && (
                                            <Box flex={0.5} px={1} alignItems="center" justifyContent="center" pt={3}>
                                                <Pressable
                                                    onPress={() => removeRow(index)}
                                                    p={1}
                                                    borderRadius="md"
                                                    _pressed={{ bg: COLORS.BORDER_COLOR }}
                                                >
                                                    <Svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                                        {deleteCardsIconSVG}
                                                    </Svg>
                                                </Pressable>
                                            </Box>
                                        )}
                                    </HStack>
                                )}
                            </Box>
                        ))}
                    </VStack>
                </Box>
            </Box>
        </ScrollView>
    );

    const getModalFooter = () => (
        <HStack space={3} justifyContent="flex-end" width="100%">
            <CustomButton
                text="Discard Changes"
                onPress={onClose}
                isOutline
                borderColor={COLORS.THEME_COLOR}
                textColor={COLORS.THEME_COLOR}
            />
            <CustomButton
                text="Submit"
                onPress={handleSubmit}
                isLoading={isLoading}
                bgColor={COLORS.THEME_COLOR}
                textColor={COLORS.WHITE_COLOR}
            />
        </HStack>
    );

    return (
        <>
            {!MobileWindow ? (
                <CustomModal
                    isOpen={isOpen}
                    onClose={onClose}
                    header={getModalHeader()}
                    body={getModalBody()}
                    footer={getModalFooter()}
                    maxW="1600px"
                    maxH="80vh"
                    width="100%"
                />
            ) : (
                <MobileModelSheet
                    ref={mobileSheetRef}
                    isOpen={isOpen}
                    onClose={onClose}
                    header={getModalHeader()}
                    body={getModalBody()}
                    footer={getModalFooter()}
                    maxH="85vh"
                />
            )}
        </>
    );
};

export default AddNewBenchmarkModal;
